import { inject, Injectable, signal } from '@angular/core';
import { AuthConfig, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { filter, take } from 'rxjs';
import { LicenseService, UserService } from 'shared/domain';
import { TreetopPropertiesProvider } from 'shared/util-common';

export type LoginOptions = {
  domainHint?: string;
  loginHint?: string;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly INTERACTIVE_LOGIN_PENDING_FLAG = 'tt-interactive-login-pending';
  // Refresh tokens expiring within this window before sending them: the API accepts recently expired tokens
  // (default clock skew), but the Core service it forwards them to does not.
  private static readonly ACCESS_TOKEN_REFRESH_MARGIN_MS = 60 * 1000;

  private oauth = inject(OAuthService);
  private userService = inject(UserService);
  private licenseService = inject(LicenseService);

  public authenticated = signal(false);

  private initialized = false;
  private initializedWithIdToken: string | null = null;
  private tokenRefresh: Promise<void> | null = null;

  private authConfig: AuthConfig = {
    responseType: 'code',
    strictDiscoveryDocumentValidation: false,
    showDebugInformation: true,
    clearHashAfterLogin: true,
    skipIssuerCheck: true,
    preserveRequestedRoute: true,
    redirectUri: window.location.origin + '/login',
    clientId: TreetopPropertiesProvider.properties.authB2C.clientId,
    scope: TreetopPropertiesProvider.properties.authB2C.defaultScopeName,
    issuer: `https://${TreetopPropertiesProvider.properties.authB2C.tenant}.${TreetopPropertiesProvider.properties.authB2C.instance}/${TreetopPropertiesProvider.properties.authB2C.tenantId}/v2.0/`,
  };

  constructor() {
    this.registerEventHandlers();

    // The license (like any other user-scoped request) needs the user to be initialised on the server first.
    // Loading it in parallel with init makes the API resolve the same new user against Core concurrently.
    this.userService.currentUser$
      .pipe(
        filter((user) => !!user),
        take(1),
      )
      .subscribe(() => this.licenseService.loadLicense());
  }

  public async init() {
    this.oauth.configure(this.authConfig);

    const discoveryUrl = `${this.oauth.issuer}.well-known/openid-configuration?p=${TreetopPropertiesProvider.properties.authB2C.signupsigninflow}`;

    await this.oauth.loadDiscoveryDocument(discoveryUrl);
    await this.oauth.tryLogin();

    this.oauth.setupAutomaticSilentRefresh();

    this.initialized = true;

    if (this.oauth.hasValidAccessToken()) {
      this.initializeSession();
    }
  }

  /**
   * Initialises the user once per ID token, and only after {@link init} has completed.
   * The OAuth library emits several events per token response ('token_received' and 'token_refreshed' back to back)
   * and again on every silent refresh; triggering the switchMap-based loader on each of them aborts the in-flight
   * request and re-sends it. Once the user is initialised, refreshed tokens don't trigger it again; if it failed,
   * the next ID token retries it.
   */
  private initializeSession(): void {
    if (!this.initialized || this.userService.user()) {
      return;
    }

    const idToken = this.oauth.getIdToken();
    if (!idToken || idToken === this.initializedWithIdToken) {
      return;
    }

    this.initializedWithIdToken = idToken;
    this.userService.initUser(idToken);
  }

  /**
   * Refreshes the access token if it has expired or is about to, so API requests aren't sent with a stale token.
   * Concurrent callers share one refresh. Resolves in any case; a failed refresh is handled by the
   * 'token_refresh_error' event.
   */
  public ensureFreshAccessToken(): Promise<void> {
    if (!this.initialized || !this.oauth.getAccessToken() || !this.oauth.getRefreshToken()) {
      return Promise.resolve();
    }

    if (this.oauth.getAccessTokenExpiration() - Date.now() > AuthService.ACCESS_TOKEN_REFRESH_MARGIN_MS) {
      return Promise.resolve();
    }

    this.tokenRefresh ??= this.oauth
      .refreshToken()
      .then(
        () => undefined,
        () => undefined,
      )
      .finally(() => {
        this.tokenRefresh = null;
      });

    return this.tokenRefresh;
  }

  private registerEventHandlers(): void {
    this.oauth.events.subscribe((event: OAuthEvent) => {
      console.debug('AuthService: OAuth event: ' + event.type);

      const isValid = this.oauth.hasValidAccessToken();

      this.authenticated.set(isValid);

      if (isValid) {
        this.initializeSession();
      }

      if (event.type === 'token_refresh_error' || event.type === 'silent_refresh_error' || event.type === 'session_terminated') {
        this.logout();
      }
    });
  }

  public async login(redirectUrl?: string, options?: LoginOptions) {
    if (!this.initialized) {
      await this.init();
    }

    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(AuthService.INTERACTIVE_LOGIN_PENDING_FLAG, '1');
      }
    } catch {
      // sessionStorage may be unavailable (e.g. private browsing restrictions)
    }

    const customQueryParams: Record<string, string> = {};

    if (options?.domainHint) {
      customQueryParams['domain_hint'] = options.domainHint;
    }

    if (options?.loginHint) {
      customQueryParams['login_hint'] = options.loginHint;
    }

    this.oauth.initLoginFlow(redirectUrl, Object.keys(customQueryParams).length > 0 ? customQueryParams : undefined);
  }

  public getAccessToken(): string {
    return this.oauth.getAccessToken();
  }

  public logout(): void {
    this.authenticated.set(false);
    this.initializedWithIdToken = null;

    this.userService.logoutUser().subscribe(() => {
      this.oauth.logOut();
      localStorage.clear();

      console.debug('AuthService: token invalidated');
    });
  }
}
